import { useState } from 'react';
import { Palette, Sparkles } from 'lucide-react';
import type { EsquemaHarmonia } from '@miolos/core';
import {
  CLIMAS,
  ESQUEMAS_HARMONIA,
  EXPLICACAO_ESQUEMA,
  ROTULO_ESQUEMA,
  misturar,
  matizesDoEsquema,
  sugerirCorPrincipal,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/switch';
import { LinhaSwitch } from '@/components/ui/switch';
import { CartaoOpcao } from '@/components/CartaoOpcao';
import { useEditor } from '../EstadoEditor';
import { AmostraCor, Grade, TituloPasso } from '../ui';

type ChaveCor =
  | 'principal'
  | 'principalEscuro'
  | 'principalClaro'
  | 'destaque'
  | 'destaqueSuave'
  | 'fundo'
  | 'fundoAlt'
  | 'texto'
  | 'textoSuave'
  | 'linha';

const ROTULOS_PALETA: Array<{ chave: ChaveCor; rotulo: string }> = [
  { chave: 'principal', rotulo: 'Cor principal' },
  { chave: 'principalEscuro', rotulo: 'Principal escura' },
  { chave: 'principalClaro', rotulo: 'Principal clara' },
  { chave: 'destaque', rotulo: 'Destaque' },
  { chave: 'destaqueSuave', rotulo: 'Destaque suave' },
  { chave: 'fundo', rotulo: 'Fundo do papel' },
  { chave: 'fundoAlt', rotulo: 'Fundo alternativo' },
  { chave: 'texto', rotulo: 'Texto' },
  { chave: 'textoSuave', rotulo: 'Texto secundário' },
  { chave: 'linha', rotulo: 'Linhas e bordas' },
];

export function PassoCores() {
  const { config, paleta, definirCores } = useEditor();
  const [clima, setClima] = useState<string>('natural');
  const [destaqueManual, setDestaqueManual] = useState(Boolean(config.cores.corDestaque));

  const classificarContraste = (razao: number) => {
    if (razao >= 7) return { rotulo: 'AAA · excelente', tom: 'sucesso' as const };
    if (razao >= 4.5) return { rotulo: 'AA · bom', tom: 'atencao' as const };
    return { rotulo: 'Baixo · ajuste', tom: 'destaque' as const };
  };

  const notaTexto = classificarContraste(paleta.contrasteTexto);

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="Cores: uma decisão só, resultado coerente"
        descricao="Você escolhe uma cor. Nós aplicamos teoria das cores para criar a paleta inteira — destaque, linhas, fundos e texto — sempre com contraste garantido para impressão."
      />

      {/* Cor principal */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <Palette className="size-4 text-primary" />
          <h3 className="text-sm font-semibold">Cor principal</h3>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-3">
            <input
              type="color"
              aria-label="Escolher cor principal"
              value={paleta.principal}
              onChange={(evento) => definirCores({ corPrincipal: evento.target.value })}
              className="size-12 cursor-pointer rounded-md border border-input bg-transparent p-1"
            />
            <Campo rotulo="Código hex">
              <Input
                value={config.cores.corPrincipal}
                onChange={(evento) => definirCores({ corPrincipal: evento.target.value })}
                className="w-32 font-mono text-xs uppercase"
                maxLength={7}
              />
            </Campo>
          </div>

          <div className="flex items-end gap-2">
            <Campo rotulo="Clima da paleta">
              <Select value={clima} onValueChange={setClima}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLIMAS.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
            <Button
              variant="contorno"
              onClick={() => definirCores({ corPrincipal: sugerirCorPrincipal(clima) })}
            >
              <Sparkles />
              Sugerir cor
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground">
          {CLIMAS.find((item) => item.id === clima)?.descricao}
        </p>
      </section>

      {/* Esquema de harmonia */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Esquema de harmonia</h3>
        <Grade colunas={2}>
          {ESQUEMAS_HARMONIA.map((esquema: EsquemaHarmonia) => {
            const automatico = esquema === 'AUTO';
            const matizes = automatico
              ? matizesDoEsquema(paleta.principal, paleta.esquemaAplicado)
              : matizesDoEsquema(paleta.principal, esquema);

            return (
              <CartaoOpcao
                key={esquema}
                selecionado={config.cores.esquema === esquema}
                titulo={ROTULO_ESQUEMA[esquema]}
                descricao={automatico ? 'Analisamos saturação e matiz e escolhemos o melhor equilíbrio.' : EXPLICACAO_ESQUEMA[esquema]}
                aoSelecionar={() => definirCores({ esquema })}
              >
                <div className="flex gap-1.5">
                  {matizes.map((cor, i) => (
                    <AmostraCor key={i} cor={cor} className="size-6" />
                  ))}
                  {automatico ? (
                    <Badge tom="principal" className="ml-1 self-center">
                      {ROTULO_ESQUEMA[paleta.esquemaAplicado]}
                    </Badge>
                  ) : null}
                </div>
              </CartaoOpcao>
            );
          })}
        </Grade>
      </section>

      {/* Destaque + ajustes finos */}
      <section className="space-y-3">
        <h3 className="text-sm font-semibold">Ajustes finos</h3>

        <LinhaSwitch
          id="destaque-manual"
          rotulo="Escolher a cor de destaque manualmente"
          descricao="Desligado, a cor de destaque vem do esquema de harmonia escolhido."
          checked={destaqueManual}
          onCheckedChange={(valor) => {
            setDestaqueManual(valor);
            definirCores({ corDestaque: valor ? paleta.destaque : null });
          }}
        />

        {destaqueManual ? (
          <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
            <input
              type="color"
              aria-label="Cor de destaque"
              value={config.cores.corDestaque ?? paleta.destaque}
              onChange={(evento) => definirCores({ corDestaque: evento.target.value })}
              className="size-10 cursor-pointer rounded-md border border-input bg-transparent p-1"
            />
            <Input
              value={config.cores.corDestaque ?? ''}
              onChange={(evento) => definirCores({ corDestaque: evento.target.value })}
              className="w-32 font-mono text-xs uppercase"
              maxLength={7}
            />
            <span className="text-xs text-muted-foreground">
              Usada em títulos de caixa, réguas e destaques do calendário.
            </span>
          </div>
        ) : null}

        <div className="space-y-2 rounded-md border border-border bg-card p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Intensidade da cor no fundo</span>
            <span className="font-mono text-xs text-muted-foreground">
              {Math.round(config.cores.intensidadeFundo * 100)}%
            </span>
          </div>
          <Slider
            value={[config.cores.intensidadeFundo]}
            min={0}
            max={1}
            step={0.05}
            onValueChange={([valor]) => definirCores({ intensidadeFundo: valor ?? 0.45 })}
          />
          <p className="text-xs leading-relaxed text-muted-foreground">
            Em vez de branco puro, o fundo recebe um toque do seu matiz. É o que faz a página parecer
            desenhada por um designer — e economiza tinta em relação a fundos coloridos fortes.
          </p>
        </div>

        <LinhaSwitch
          id="contraste-alto"
          rotulo="Garantir contraste alto (7:1)"
          descricao="Escurece o texto até atingir o nível AAA de leitura. Recomendado para impressão em papel comum."
          checked={config.cores.contrasteAlto}
          onCheckedChange={(valor) => definirCores({ contrasteAlto: valor })}
        />
      </section>

      {/* Paleta resultante */}
      <section className="space-y-3 rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Paleta gerada</h3>
          <div className="flex items-center gap-2">
            <Badge tom={notaTexto.tom}>Texto/fundo {paleta.contrasteTexto}:1 · {notaTexto.rotulo}</Badge>
            <Badge tom={paleta.contrastePrincipal >= 4.5 ? 'sucesso' : 'atencao'}>
              Texto sobre a cor principal {paleta.contrastePrincipal}:1
            </Badge>
          </div>
        </div>

        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {ROTULOS_PALETA.map(({ chave, rotulo }) => (
            <li key={chave} className="flex items-center gap-2 rounded-md border border-border p-2">
              <AmostraCor cor={paleta[chave]} />
              <div className="min-w-0">
                <p className="truncate text-xs font-medium">{rotulo}</p>
                <p className="font-mono text-[10px] uppercase text-muted-foreground">{paleta[chave]}</p>
              </div>
            </li>
          ))}
        </ul>

        <p className="rounded-md bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
          {paleta.harmoniaExplicacao}
        </p>

        <div className="flex items-center gap-2">
          <span className="shrink-0 text-xs text-muted-foreground">Prévia da capa:</span>
          <div
            className="h-8 flex-1 rounded-md"
            style={{
              background: `linear-gradient(135deg, ${paleta.principalEscuro} 0%, ${paleta.destaque} 100%)`,
            }}
          />
          <div className="flex h-8 w-28 items-center rounded-md border border-border px-2" style={{ background: paleta.fundo }}>
            <span className="text-[10px] font-medium" style={{ color: paleta.texto }}>
              Texto sobre o papel
            </span>
          </div>
          <div
            className="h-8 w-16 rounded-md"
            style={{ background: misturar(paleta.principal, paleta.fundo, 0.72) }}
          />
        </div>
      </section>
    </div>
  );
}
